import { Children, type ReactNode } from "react";

interface RevealStaggerProps {
  children: ReactNode;
  /** Delay before the first item starts (ms). */
  base?: number;
  /** Extra delay added per item (ms) — the cascade spacing. */
  step?: number;
}

/**
 * Linear-style staggered content reveal. Wraps each direct child in a
 * `.reveal-item` (fade in + rise + un-blur, see `styles/utilities.css`) with an
 * incremental `animation-delay`, so the children cascade top-to-bottom on
 * entrance instead of the whole block appearing at once.
 *
 * Renders a FRAGMENT (no wrapper element) on purpose: the `.reveal-item` divs
 * become direct children of whatever lays them out, so the parent's existing
 * `space-y-*` rhythm is preserved (a header block, three form fields, and a
 * button can keep their different gaps while still cascading). To replay the
 * cascade — e.g. on a wizard step change — key the PARENT element (the wizard
 * already does `<div key={phase}>`), which remounts these items.
 *
 * Respects `prefers-reduced-motion` (the `.reveal-item` animation is disabled
 * under reduce, so content is simply present).
 */
export function RevealStagger({
  children,
  base = 40,
  step = 65,
}: RevealStaggerProps) {
  const items = Children.toArray(children).filter(Boolean);
  return (
    <>
      {items.map((child, i) => (
        <div
          key={i}
          className="reveal-item"
          style={{ animationDelay: `${base + i * step}ms` }}
        >
          {child}
        </div>
      ))}
    </>
  );
}
